import React, { FC, useState } from 'react';
import { useDataProvider } from 'src/app/common/utils';
import { fetchIncentiveList, IncentiveListParams } from '../../network/incentiveCrud';
import { useDispatch } from 'react-redux';
import { IncentiveItem, PublishStatus } from '../../types/incentive-types';
import { PaginateList } from '../../../../common/types/common-types';
import { ParamsProps } from '../../../../common/components/ParamsProvider';
import { getDayStart, getDayEnd } from '../../../../common/utils/time-utils';
import IncentiveList from './components/IncentiveList';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import { useIntl } from 'react-intl';
import { map } from 'lodash';

type IncentiveListingPageProps = {} & ParamsProps;

const initialState: IncentiveListParams = {
  code: '',
  name: '',
  startDateFrom: null,
  startDateTo: null,
  endDateFrom: null,
  endDateTo: null,
  createdDateFrom: null,
  createdDateTo: null,
  status: '',
  type: '',
  isArchived: 'false',
  page: 0,
  limit: 5
};

export const incentiveFilterKeys = ["code", "name", "startDateFrom", "startDateTo", "endDateFrom", "endDateTo", "createdDateFrom", "createdDateTo", "status", "type", "isArchived"];

const paramsInitiator = (initialParams?: Record<string, string>): IncentiveListParams => {
  return initialParams ? {
    code: initialParams.code ?? '',
    name: initialParams.name ?? '',
    startDateFrom: initialParams.startDateFrom ? getDayStart(new Date(initialParams.startDateFrom)) : null,
    startDateTo: initialParams.startDateTo ? getDayEnd(new Date(initialParams.startDateTo)) : null,
    endDateFrom: initialParams.endDateFrom ? getDayStart(new Date(initialParams.endDateFrom)) : null,
    endDateTo: initialParams.endDateTo ? getDayEnd(new Date(initialParams.endDateTo)) : null,
    createdDateFrom: initialParams.createdDateFrom ? getDayStart(new Date(initialParams.createdDateFrom)) : null,
    createdDateTo: initialParams.createdDateTo ? getDayEnd(new Date(initialParams.createdDateTo)) : null,
    status: initialParams.status ?? '',
    type: initialParams.type ?? '',
    isArchived: initialParams.isArchived ?? 'false',
    page: 0,
    limit: 5
  } : initialState;
}

const IncentiveListingPage: FC<IncentiveListingPageProps> = ({ initialParams, onChangeQueryParams }) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const [filterState, setFilterState] = useState<IncentiveListParams>(paramsInitiator(initialParams));
  const [incentiveList, setIncentiveList] = useState<PaginateList<IncentiveItem>>();

  const { isLoading, refreshData } = useDataProvider<PaginateList<IncentiveItem>>(async () => {
    try {
      if (onChangeQueryParams) {
        onChangeQueryParams(filterState);
      }
      return await fetchIncentiveList(filterState, dispatch);
    } catch (err) {}
  }, setIncentiveList, false);

  return (
    <>
      <PruFilter
        title={Translation("incentive.filter")}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: "code",
            initialValue: filterState.code,
            displayName: Translation("incentive.common.code")
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            field: "name",
            initialValue: filterState.name,
            displayName: Translation("incentive.common.name")
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: "status",
            initialValue: filterState.status,
            displayName: Translation("incentive.common.status"),
            choices: [
              {displayName: "", value: ""},
              ...map(PublishStatus, (option: string) => ({ displayName: option, value: option }))
            ]
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: "createdDateFrom",
            fieldTo: "createdDateTo",
            initialDateFrom: filterState.createdDateFrom,
            initialDateTo: filterState.createdDateTo,
            displayName: Translation("incentive.common.createdDate")
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: "startDateFrom",
            fieldTo: "startDateTo",
            initialDateFrom: filterState.startDateFrom,
            initialDateTo: filterState.startDateTo,
            displayName: Translation("incentive.common.startDate")
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: "endDateFrom",
            fieldTo: "endDateTo",
            initialDateFrom: filterState.endDateFrom,
            initialDateTo: filterState.endDateTo,
            displayName: Translation("incentive.common.endDate")
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: "isArchived",
            defaultValue: "false",
            initialValue: filterState.isArchived,
            displayName: Translation("incentive.common.isArchived"),
            choices: [
              { displayName: "", value: "" },
              { displayName: "True", value: "true" },
              { displayName: "False", value: "false" }
            ]
          }
        ]}
        onChangeFilter={data => setFilterState({
          ...filterState,
          code: data.code,
          name: data.name,
          startDateFrom: data.startDateFrom,
          startDateTo: data.startDateTo,
          endDateFrom: data.endDateFrom,
          endDateTo: data.endDateTo,
          createdDateFrom: data.createdDateFrom,
          createdDateTo: data.createdDateTo,
          status: data.status || '',
          type: data.type || '',
          isArchived: data.isArchived
        })}
        fetchData={refreshData}
      />
      <IncentiveList
        isLoading={isLoading}
        onRefresh={refreshData}
        incentiveList={incentiveList}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage
          });
          refreshData();
        }}
      />
    </>
  )
};

export default IncentiveListingPage;